import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type {
  AnalysedService,
  DeploymentPlan,
  DeploymentView,
  GitHubInstallationView,
  GitHubRepositoryView,
  ProviderCapability,
  ProviderConnectionView,
  RepositoryAnalysis,
  SessionView,
  SetupState,
} from '../../shared/contracts';
import { api, loadSession, post, remove } from '../api';
import {
  Badge,
  Button,
  EmptyState,
  ErrorNotice,
  Field,
  Mark,
  Skeleton,
  Spinner,
} from '../components/ui';
import { stateTone } from '../components/tone';

type Step = 'repository' | 'analysis' | 'provider' | 'configuration' | 'review' | 'deployment';
interface AnalysisResponse {
  projectId: string;
  analysisIds: string[];
  analysis: RepositoryAnalysis;
}
const STEPS: Array<{ key: Step; label: string }> = [
  { key: 'repository', label: 'Repository' },
  { key: 'analysis', label: 'Analysis' },
  { key: 'provider', label: 'Provider' },
  { key: 'configuration', label: 'Configuration' },
  { key: 'review', label: 'Review' },
  { key: 'deployment', label: 'Deployment' },
];
const shortSha = (value: string) => value.slice(0, 8);

function Header({ session, onSettings }: { session: SessionView; onSettings: () => void }) {
  return (
    <header className="app-header">
      <Link className="brand" to="/">
        <Mark compact />
        <span>RepoHost</span>
      </Link>
      <div className="app-header__right">
        <span className="environment-pill">
          <span /> Control plane
        </span>
        <button className="account-button" onClick={onSettings} aria-label="Open settings">
          {session.user?.avatarUrl ? (
            <img src={session.user.avatarUrl} alt="" />
          ) : (
            <span>{session.user?.login.slice(0, 1).toUpperCase()}</span>
          )}
          <span>{session.user?.login}</span>
        </button>
      </div>
    </header>
  );
}

function SetupChecklist({ setup }: { setup: SetupState }) {
  return (
    <div className="setup-page" id="main-content">
      <div className="section-heading">
        <span>Operator setup</span>
        <h1>Finish the control-plane configuration</h1>
        <p>RepoHost stays closed until every required server binding is present.</p>
      </div>
      <div className="checklist">
        {setup.checks.map((check) => (
          <div key={check.key} className="check-row">
            <span className={check.configured ? 'check check--done' : 'check'}>
              {check.configured ? '✓' : '!'}
            </span>
            <div>
              <strong>{check.label}</strong>
              <p>{check.help}</p>
            </div>
            <Badge tone={check.configured ? 'good' : 'warn'}>
              {check.configured ? 'Configured' : 'Missing'}
            </Badge>
          </div>
        ))}
      </div>
      <p className="muted">
        See the README setup sequence for exact GitHub App fields, D1 and Queue commands, and secret
        names.
      </p>
    </div>
  );
}

function RepositoryStep({
  installations,
  onInstall,
  onAnalysed,
}: {
  installations: GitHubInstallationView[];
  onInstall: () => Promise<void>;
  onAnalysed: (value: AnalysisResponse) => void;
}) {
  const [repositories, setRepositories] = useState<
    Array<GitHubRepositoryView & { installationId: number }>
  >([]);
  const [branches, setBranches] = useState<string[]>([]);
  const [repositoryId, setRepositoryId] = useState<number | null>(null);
  const [branch, setBranch] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const pageSize = 7;
  const filtered = useMemo(
    () => repositories.filter((item) => item.fullName.toLowerCase().includes(search.toLowerCase())),
    [repositories, search],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const selected = repositories.find((item) => item.id === repositoryId);
  const sync = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const lists = await Promise.all(
        installations
          .filter((item) => !item.suspended)
          .map((item) =>
            post<Array<GitHubRepositoryView & { installationId: number }>>(
              `/api/github/installations/${item.id}/sync`,
              {},
            ),
          ),
      );
      setRepositories(lists.flat().sort((a, b) => a.fullName.localeCompare(b.fullName)));
    } catch (next) {
      setError(next);
    } finally {
      setLoading(false);
    }
  }, [installations]);
  useEffect(() => {
    if (installations.length > 0) void sync();
  }, [installations.length, sync]);
  useEffect(() => {
    if (!selected) return;
    setBranches([]);
    setBranch(selected.defaultBranch);
    void api<string[]>(`/api/github/repositories/${selected.id}/branches`)
      .then(setBranches)
      .catch(setError);
  }, [selected]);
  async function analyse() {
    if (!selected || !branch) return;
    setAnalysing(true);
    setError(null);
    try {
      onAnalysed(
        await post('/api/analyses', {
          repositoryId: selected.id,
          branch,
          projectName: selected.name,
        }),
      );
    } catch (next) {
      setError(next);
    } finally {
      setAnalysing(false);
    }
  }
  if (installations.length === 0)
    return (
      <EmptyState
        title="Install the GitHub App"
        action={<Button onClick={() => void onInstall()}>Choose repository access</Button>}
      >
        No GitHub installation is linked to this owner session. Grant all repositories or a selected
        subset in GitHub.
      </EmptyState>
    );
  return (
    <div>
      <div className="step-title">
        <div>
          <span>Source</span>
          <h1>Select a repository</h1>
          <p>Only repositories granted to the linked GitHub App are available.</p>
        </div>
        <Button className="button--quiet" onClick={() => void onInstall()}>
          Change GitHub access
        </Button>
      </div>
      {error ? <ErrorNotice error={error} onRetry={() => void sync()} /> : null}
      <div className="toolbar">
        <label className="search">
          <span aria-hidden="true">⌕</span>
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search authorised repositories"
            aria-label="Search authorised repositories"
          />
        </label>
        <Button className="button--quiet" onClick={() => void sync()} disabled={loading}>
          {loading ? 'Syncing…' : 'Sync from GitHub'}
        </Button>
      </div>
      {loading && repositories.length === 0 ? (
        <Skeleton lines={5} />
      ) : filtered.length === 0 ? (
        <EmptyState title="No matching repositories">
          Check the GitHub installation’s selected-repository access, then sync again.
        </EmptyState>
      ) : (
        <div className="repo-list" role="radiogroup" aria-label="Repositories">
          {filtered.slice((page - 1) * pageSize, page * pageSize).map((repo) => (
            <button
              key={repo.id}
              role="radio"
              aria-checked={repo.id === repositoryId}
              className={`repo-row ${repo.id === repositoryId ? 'repo-row--selected' : ''}`}
              onClick={() => setRepositoryId(repo.id)}
            >
              <span className="repo-icon" aria-hidden="true">
                ◇
              </span>
              <span>
                <strong>{repo.fullName}</strong>
                <small>
                  {repo.private ? 'Private repository' : 'Public repository'} · default branch{' '}
                  {repo.defaultBranch}
                </small>
              </span>
              <Badge tone={repo.private ? 'neutral' : 'info'}>
                {repo.private ? 'Private' : 'Public'}
              </Badge>
              <span className="radio-dot" />
            </button>
          ))}
        </div>
      )}
      <div className="pagination">
        <Button
          className="button--quiet button--small"
          disabled={page <= 1}
          onClick={() => setPage((value) => value - 1)}
        >
          Previous
        </Button>
        <span>
          Page {page} of {pages}
        </span>
        <Button
          className="button--quiet button--small"
          disabled={page >= pages}
          onClick={() => setPage((value) => value + 1)}
        >
          Next
        </Button>
      </div>
      {selected ? (
        <div className="selection-bar">
          <div>
            <strong>{selected.fullName}</strong>
            <span>Choose the immutable branch snapshot to analyse.</span>
          </div>
          <select
            value={branch}
            onChange={(event) => setBranch(event.target.value)}
            aria-label="Branch"
          >
            {(branches.length ? branches : [selected.defaultBranch]).map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
          <Button onClick={() => void analyse()} disabled={analysing}>
            {analysing ? <Spinner label="Analysing" /> : 'Analyse repository'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function AnalysisStep({
  response,
  serviceIndex,
  setServiceIndex,
  corrections,
  setCorrections,
  next,
}: {
  response: AnalysisResponse;
  serviceIndex: number;
  setServiceIndex: (value: number) => void;
  corrections: Record<string, string | null>;
  setCorrections: (value: Record<string, string | null>) => void;
  next: () => void;
}) {
  const service = response.analysis.services[serviceIndex];
  if (!service)
    return (
      <EmptyState title="Nothing deployable was detected">
        Review the inspected files and warnings. RepoHost will not guess a deployment contract
        without a supported service signal.
      </EmptyState>
    );
  const edit = (key: string, value: string) =>
    setCorrections({ ...corrections, [key]: value === '' ? null : value });
  return (
    <div>
      <div className="step-title">
        <div>
          <span>Deterministic analysis</span>
          <h1>Review what RepoHost detected</h1>
          <p>
            Snapshot <code>{shortSha(response.analysis.commitSha)}</code> on{' '}
            <code>{response.analysis.branch}</code>. No repository code was executed.
          </p>
        </div>
        <Badge tone={response.analysis.monorepo.detected ? 'info' : 'neutral'}>
          {response.analysis.monorepo.detected ? 'Monorepo' : 'Single service'}
        </Badge>
      </div>
      {response.analysis.globalWarnings.map((warning) => (
        <div className="notice notice--warn" key={warning}>
          {warning}
        </div>
      ))}
      {response.analysis.services.length > 1 ? (
        <div className="service-tabs" role="tablist">
          {response.analysis.services.map((item, index) => (
            <button
              role="tab"
              aria-selected={index === serviceIndex}
              key={item.id}
              onClick={() => setServiceIndex(index)}
            >
              {item.proposedName}
              <Badge tone={stateTone(item.compatibility)}>
                {item.compatibility.replace('_', ' ')}
              </Badge>
            </button>
          ))}
        </div>
      ) : null}
      <div className="analysis-grid">
        <section className="panel">
          <div className="panel__header">
            <div>
              <span className="overline">Service contract</span>
              <h2>{service.proposedName}</h2>
            </div>
            <Badge tone={stateTone(service.compatibility)}>
              {service.compatibility.replaceAll('_', ' ')}
            </Badge>
          </div>
          <dl className="facts">
            <div>
              <dt>Framework</dt>
              <dd>{service.framework}</dd>
            </div>
            <div>
              <dt>Runtime</dt>
              <dd>{service.runtime}</dd>
            </div>
            <div>
              <dt>Kind</dt>
              <dd>{service.kind.replace('_', ' ')}</dd>
            </div>
            <div>
              <dt>Root</dt>
              <dd>
                <code>{service.rootDirectory}</code>
              </dd>
            </div>
          </dl>
          {service.reasons.map((reason) => (
            <div
              className={`notice ${service.compatibility === 'unsupported' ? 'notice--bad' : 'notice--warn'}`}
              key={reason}
            >
              {reason}
            </div>
          ))}
        </section>
        <section className="panel">
          <div className="panel__header">
            <div>
              <span className="overline">Evidence</span>
              <h2>Why it was inferred</h2>
            </div>
            <Badge>{service.evidence.length} files</Badge>
          </div>
          <ul className="evidence-list">
            {service.evidence.map((item) => (
              <li key={`${item.file}-${item.detail}`}>
                <code>{item.file}</code>
                <span>{item.detail}</span>
              </li>
            ))}
          </ul>
          <details>
            <summary>Inspection boundary</summary>
            <p className="muted">
              Parsed {response.analysis.inspectedFiles.length} relevant files. Values from real
              environment files were never requested.
            </p>
          </details>
        </section>
      </div>
      <section className="panel panel--wide">
        <div className="panel__header">
          <div>
            <span className="overline">Editable corrections</span>
            <h2>Build and run commands</h2>
          </div>
          <span className="muted">Inferences stay attributable after edits.</span>
        </div>
        <div className="form-grid">
          <Field label="Root directory" hint={`Inferred from ${service.rootDirectory}`}>
            <input
              value={String(corrections.rootDirectory ?? service.rootDirectory)}
              onChange={(event) => edit('rootDirectory', event.target.value)}
            />
          </Field>
          <Field label="Install command" hint={service.installCommand.source}>
            <input
              value={String(corrections.installCommand ?? service.installCommand.value ?? '')}
              onChange={(event) => edit('installCommand', event.target.value)}
            />
          </Field>
          <Field label="Build command" hint={service.buildCommand.source}>
            <input
              value={String(corrections.buildCommand ?? service.buildCommand.value ?? '')}
              onChange={(event) => edit('buildCommand', event.target.value)}
            />
          </Field>
          {service.kind === 'static' ? (
            <Field label="Output directory" hint={service.outputDirectory.source}>
              <input
                value={String(corrections.outputDirectory ?? service.outputDirectory.value ?? '')}
                onChange={(event) => edit('outputDirectory', event.target.value)}
              />
            </Field>
          ) : (
            <Field label="Start command" hint={service.startCommand.source}>
              <input
                value={String(corrections.startCommand ?? service.startCommand.value ?? '')}
                onChange={(event) => edit('startCommand', event.target.value)}
              />
            </Field>
          )}
        </div>
      </section>
      <div className="step-actions">
        <span>
          {service.compatibility === 'unsupported'
            ? 'This service cannot proceed without resolving the blockers.'
            : 'Correct any low-confidence inference before planning.'}
        </span>
        <Button onClick={next} disabled={service.compatibility === 'unsupported'}>
          Choose provider <span aria-hidden="true">→</span>
        </Button>
      </div>
    </div>
  );
}

function ProviderStep({
  service,
  capabilities,
  connections,
  reload,
  selectedConnection,
  setSelectedConnection,
  next,
}: {
  service: AnalysedService;
  capabilities: ProviderCapability[];
  connections: ProviderConnectionView[];
  reload: () => Promise<void>;
  selectedConnection: string;
  setSelectedConnection: (value: string) => void;
  next: () => void;
}) {
  const compatible = service.providerCandidates
    .filter((item) => item.status !== 'unsupported')
    .map((item) => item.provider);
  const [provider, setProvider] = useState<'render' | 'cloudflare_pages'>(
    compatible[0] ?? 'render',
  );
  const [showConnect, setShowConnect] = useState(connections.length === 0);
  const [apiKey, setApiKey] = useState('');
  const [accountId, setAccountId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const eligibleConnections = useMemo(
    () => connections.filter((item) => item.provider === provider),
    [connections, provider],
  );
  useEffect(() => {
    if (!eligibleConnections.some((item) => item.id === selectedConnection))
      setSelectedConnection(eligibleConnections[0]?.id ?? '');
  }, [eligibleConnections, selectedConnection, setSelectedConnection]);
  async function connect() {
    setSaving(true);
    setError(null);
    try {
      const connection =
        provider === 'render'
          ? await post<ProviderConnectionView>('/api/providers/connections', {
              provider,
              apiKey,
              workspaceId: accountId,
            })
          : await post<ProviderConnectionView>('/api/providers/connections', {
              provider,
              apiToken: apiKey,
              accountId,
            });
      setApiKey('');
      setSelectedConnection(connection.id);
      setShowConnect(false);
      await reload();
    } catch (nextError) {
      setError(nextError);
    } finally {
      setSaving(false);
    }
  }
  return (
    <div>
      <div className="step-title">
        <div>
          <span>Destination</span>
          <h1>Connect a supported provider</h1>
          <p>Resources are created in your account. RepoHost never collects payment details.</p>
        </div>
      </div>
      <div className="provider-grid">
        {capabilities.map((item) => {
          const support = service.providerCandidates.find(
            (candidate) => candidate.provider === item.provider,
          );
          const enabled = support?.status !== 'unsupported';
          return (
            <button
              key={item.provider}
              className={`provider-card ${provider === item.provider ? 'provider-card--selected' : ''}`}
              disabled={!enabled}
              onClick={() => setProvider(item.provider)}
            >
              <span className={`provider-symbol provider-symbol--${item.provider}`}>
                {item.provider === 'render' ? 'R' : 'CF'}
              </span>
              <span>
                <strong>{item.displayName}</strong>
                <small>
                  {item.provider === 'render'
                    ? 'Static sites and stateless web services'
                    : 'Static frontends only'}
                </small>
              </span>
              <Badge tone={enabled ? 'good' : 'bad'}>
                {enabled ? 'Compatible' : 'Unsupported'}
              </Badge>
            </button>
          );
        })}
      </div>
      {error ? <ErrorNotice error={error} /> : null}
      {showConnect ? (
        <section className="panel connection-form">
          <div className="panel__header">
            <div>
              <span className="overline">Write-only credential</span>
              <h2>Connect {provider === 'render' ? 'Render' : 'Cloudflare Pages'}</h2>
            </div>
          </div>
          <div className="notice notice--warn">
            <div>
              <strong>Treat this as a high-impact credential.</strong>
              <p>
                {provider === 'render'
                  ? 'A Render API key can access every workspace available to its owner. Use a dedicated account where practical.'
                  : 'Create a narrowly scoped token with Account → Cloudflare Pages → Edit for only the intended account.'}{' '}
                Encryption at rest limits database exposure; it does not make a compromised control
                plane harmless.
              </p>
            </div>
          </div>
          <div className="form-grid">
            <Field
              label={provider === 'render' ? 'Render API key' : 'Cloudflare API token'}
              hint="Sent once, then encrypted server-side."
            >
              <input
                type="password"
                autoComplete="off"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
              />
            </Field>
            <Field
              label={provider === 'render' ? 'Workspace ID' : 'Cloudflare account ID'}
              hint="Copy this from the provider dashboard."
            >
              <input
                autoComplete="off"
                value={accountId}
                onChange={(event) => setAccountId(event.target.value)}
              />
            </Field>
          </div>
          <div className="step-actions">
            <a
              className="text-link"
              target="_blank"
              rel="noreferrer"
              href={
                provider === 'render'
                  ? 'https://render.com/docs/api'
                  : 'https://developers.cloudflare.com/fundamentals/api/get-started/create-token/'
              }
            >
              Credential documentation ↗
            </a>
            <Button disabled={!apiKey || !accountId || saving} onClick={() => void connect()}>
              {saving ? <Spinner label="Validating" /> : 'Validate and connect'}
            </Button>
          </div>
        </section>
      ) : (
        <section className="panel">
          <div className="panel__header">
            <div>
              <span className="overline">Verified connection</span>
              <h2>Select an account</h2>
            </div>
            <Button className="button--quiet button--small" onClick={() => setShowConnect(true)}>
              Connect another
            </Button>
          </div>
          {eligibleConnections.length ? (
            <div className="connection-list">
              {eligibleConnections.map((item) => (
                <label key={item.id}>
                  <input
                    type="radio"
                    name="connection"
                    checked={selectedConnection === item.id}
                    onChange={() => setSelectedConnection(item.id)}
                  />
                  <span>
                    <strong>{item.accountLabel}</strong>
                    <small>Verified {new Date(item.lastVerifiedAt).toLocaleString()}</small>
                  </span>
                  <Badge tone="good">Validated</Badge>
                </label>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No connection for this provider"
              action={<Button onClick={() => setShowConnect(true)}>Connect account</Button>}
            >
              Add and validate a credential before continuing.
            </EmptyState>
          )}
        </section>
      )}
      <section className="limit-panel">
        <div>
          <span className="overline">Free-tier disclosure</span>
          <h3>{capabilities.find((item) => item.provider === provider)?.freeTier.summary}</h3>
        </div>
        <ul>
          {capabilities
            .find((item) => item.provider === provider)
            ?.freeTier.limitations.slice(0, 3)
            .map((item) => (
              <li key={item}>{item}</li>
            ))}
        </ul>
      </section>
      <div className="step-actions">
        <span>Private-repository access is a separate provider prerequisite.</span>
        <Button disabled={!selectedConnection} onClick={next}>
          Configure service <span aria-hidden="true">→</span>
        </Button>
      </div>
    </div>
  );
}

function ConfigurationStep({
  service,
  values,
  setValues,
  autoDeploy,
  setAutoDeploy,
  onCreatePlan,
  loading,
  error,
}: {
  service: AnalysedService;
  values: Record<string, string>;
  setValues: (value: Record<string, string>) => void;
  autoDeploy: boolean;
  setAutoDeploy: (value: boolean) => void;
  onCreatePlan: () => void;
  loading: boolean;
  error: unknown;
}) {
  const [extra, setExtra] = useState('');
  const names = [
    ...new Set([...service.environmentVariables.map((item) => item.name), ...Object.keys(values)]),
  ].sort();
  function addExtra() {
    const name = extra.trim().toUpperCase();
    if (/^[A-Z_][A-Z0-9_]*$/u.test(name) && name !== 'PORT') {
      setValues({ ...values, [name]: '' });
      setExtra('');
    }
  }
  return (
    <div>
      <div className="step-title">
        <div>
          <span>Runtime configuration</span>
          <h1>Supply environment values</h1>
          <p>
            Values are write-only. After saving, the interface retains only whether each value was
            supplied.
          </p>
        </div>
        <Badge>{names.length} variables</Badge>
      </div>
      {error ? <ErrorNotice error={error} /> : null}
      <section className="panel env-panel">
        <div className="panel__header">
          <div>
            <span className="overline">Encrypted at rest</span>
            <h2>Environment variables</h2>
          </div>
          <span className="lock-note">⌑ Values will not be shown again</span>
        </div>
        {names.length === 0 ? (
          <EmptyState title="No environment variables detected">
            Add one only if the application needs configuration not visible in bounded manifest
            inspection.
          </EmptyState>
        ) : (
          <div className="env-list">
            {names.map((name) => {
              const hint = service.environmentVariables.find((item) => item.name === name);
              return (
                <Field
                  key={name}
                  label={name}
                  hint={
                    hint
                      ? `${hint.required ? 'Required' : 'Optional'} · ${hint.purpose}`
                      : 'Owner-supplied variable'
                  }
                >
                  <div className="secret-input">
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={values[name] ?? ''}
                      onChange={(event) => setValues({ ...values, [name]: event.target.value })}
                      placeholder="Enter value"
                    />
                    <span aria-hidden="true">•••</span>
                  </div>
                </Field>
              );
            })}
          </div>
        )}
        <div className="add-variable">
          <input
            value={extra}
            onChange={(event) => setExtra(event.target.value)}
            placeholder="ADD_VARIABLE_NAME"
            aria-label="Additional variable name"
          />
          <Button className="button--quiet button--small" onClick={addExtra}>
            Add variable
          </Button>
        </div>
      </section>
      <label className="toggle-row">
        <span>
          <strong>Automatic deploys from the selected branch</strong>
          <small>
            The provider’s verified Git integration handles pushes; RepoHost will not trigger a
            duplicate build.
          </small>
        </span>
        <input
          type="checkbox"
          checked={autoDeploy}
          onChange={(event) => setAutoDeploy(event.target.checked)}
        />
        <span className="toggle" />
      </label>
      <div className="notice">
        <div>
          <strong>External dependencies stay external.</strong>
          <p>RepoHost does not create databases, Redis, queues, disks, workers, or cron jobs.</p>
        </div>
      </div>
      <div className="step-actions">
        <span>PORT is reserved and assigned by the hosting provider.</span>
        <Button onClick={onCreatePlan} disabled={loading}>
          {loading ? <Spinner label="Creating plan" /> : 'Create deployment plan'}
        </Button>
      </div>
    </div>
  );
}

function ReviewStep({
  plan,
  onConfirm,
  loading,
  error,
}: {
  plan: DeploymentPlan;
  onConfirm: () => void;
  loading: boolean;
  error: unknown;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <div>
      <div className="step-title">
        <div>
          <span>Confirmation boundary</span>
          <h1>Review the exact deployment plan</h1>
          <p>
            This plan expires {new Date(plan.expiresAt).toLocaleString()} and is locked to commit{' '}
            <code>{shortSha(plan.commitSha)}</code>.
          </p>
        </div>
        <Badge tone={plan.blockers.length ? 'bad' : 'good'}>
          {plan.blockers.length ? 'Blocked' : 'Ready'}
        </Badge>
      </div>
      {error ? <ErrorNotice error={error} /> : null}
      {plan.blockers.map((item) => (
        <div className="notice notice--bad" key={item}>
          {item}
        </div>
      ))}
      <section className="review-sheet">
        <div className="review-sheet__head">
          <div>
            <span className="provider-symbol">{plan.provider === 'render' ? 'R' : 'CF'}</span>
            <div>
              <strong>{plan.serviceName}</strong>
              <span>
                {plan.providerName} · {plan.providerAccount}
              </span>
            </div>
          </div>
          <Badge tone="good">Free plan only</Badge>
        </div>
        <dl className="review-table">
          <div>
            <dt>Source</dt>
            <dd>
              {plan.repositoryFullName}
              <small>
                {plan.branch} · {shortSha(plan.commitSha)}
              </small>
            </dd>
          </div>
          <div>
            <dt>Service</dt>
            <dd>
              {plan.serviceType.replace('_', ' ')}
              <small>Root: {plan.rootDirectory}</small>
            </dd>
          </div>
          <div>
            <dt>Build</dt>
            <dd>
              <code>
                {[plan.installCommand, plan.buildCommand].filter(Boolean).join(' && ') ||
                  'No build command'}
              </code>
            </dd>
          </div>
          {plan.startCommand ? (
            <div>
              <dt>Start</dt>
              <dd>
                <code>{plan.startCommand}</code>
              </dd>
            </div>
          ) : null}
          {plan.outputDirectory ? (
            <div>
              <dt>Publish</dt>
              <dd>
                <code>{plan.outputDirectory}</code>
              </dd>
            </div>
          ) : null}
          <div>
            <dt>Network</dt>
            <dd>
              Public HTTPS URL<small>Shown only after provider status is live</small>
            </dd>
          </div>
          <div>
            <dt>Environment</dt>
            <dd>
              {plan.environmentVariables.filter((item) => item.supplied).length} values supplied
              <small>Names retained; values encrypted and hidden</small>
            </dd>
          </div>
        </dl>
      </section>
      <section className="limit-panel">
        <div>
          <span className="overline">Cost and service limits</span>
          <h3>{plan.freeTier.summary}</h3>
        </div>
        <ul>
          {plan.freeTier.limitations.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <div className="evidence-links">
          Verified {plan.evidence.lastVerifiedAt} ·{' '}
          {plan.evidence.sourceUrls.map((url, index) => (
            <a key={url} href={url} target="_blank" rel="noreferrer">
              Source {index + 1}
            </a>
          ))}
        </div>
      </section>
      {plan.warnings.map((item) => (
        <div className="notice notice--warn" key={item}>
          {item}
        </div>
      ))}
      <label className="confirmation-check">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
        />
        <span>
          I reviewed the commands, provider prerequisites, public exposure, and free-tier limits.
          Create this resource in my connected provider account.
        </span>
      </label>
      <div className="step-actions">
        <span>
          Plan digest <code>{plan.digest.slice(0, 12)}…</code>
        </span>
        <Button disabled={!acknowledged || plan.blockers.length > 0 || loading} onClick={onConfirm}>
          {loading ? <Spinner label="Queueing" /> : 'Confirm and deploy'}
        </Button>
      </div>
    </div>
  );
}

function DeploymentStep({
  deployment,
  reload,
}: {
  deployment: DeploymentView;
  reload: () => Promise<void>;
}) {
  const [logs, setLogs] = useState<Array<{ timestamp: string | null; message: string }> | null>(
    null,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteText, setDeleteText] = useState('');
  const active = ['QUEUED', 'PROVISIONING', 'BUILDING', 'DELETE_REQUESTED', 'DELETING'].includes(
    deployment.state,
  );
  async function action(kind: 'retry' | 'redeploy' | 'refresh') {
    setBusy(true);
    setError(null);
    try {
      await post(`/api/deployments/${deployment.id}/${kind}`, {});
      await reload();
    } catch (next) {
      setError(next);
    } finally {
      setBusy(false);
    }
  }
  async function deleteResource() {
    setBusy(true);
    try {
      await remove(`/api/deployments/${deployment.id}`);
      setConfirmDelete(false);
      await reload();
    } catch (next) {
      setError(next);
    } finally {
      setBusy(false);
    }
  }
  async function loadLogs() {
    setBusy(true);
    try {
      setLogs(await api(`/api/deployments/${deployment.id}/logs`));
    } catch (next) {
      setError(next);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div>
      <div className="deployment-hero">
        <div>
          <Badge tone={stateTone(deployment.state)}>{deployment.state.replaceAll('_', ' ')}</Badge>
          <h1>{deployment.serviceName}</h1>
          <p>
            {deployment.providerName} · commit <code>{shortSha(deployment.commitSha)}</code>
          </p>
        </div>
        {active ? (
          <div className="build-indicator">
            <span />
            <div>
              <strong>Provider operation in progress</strong>
              <small>Status refreshes automatically</small>
            </div>
          </div>
        ) : null}
      </div>
      {error ? <ErrorNotice error={error} /> : null}
      {deployment.state === 'LIVE' && deployment.publicUrl ? (
        <section className="live-banner">
          <div>
            <span aria-hidden="true">✓</span>
            <div>
              <strong>Deployment is live</strong>
              <a href={deployment.publicUrl} target="_blank" rel="noreferrer">
                {deployment.publicUrl} ↗
              </a>
            </div>
          </div>
          <Button
            className="button--light"
            onClick={() => void navigator.clipboard.writeText(deployment.publicUrl ?? '')}
          >
            Copy URL
          </Button>
        </section>
      ) : null}
      {deployment.error ? (
        <div className="failure-panel">
          <div>
            <span aria-hidden="true">!</span>
            <div>
              <h2>{deployment.error.code.replaceAll('_', ' ')}</h2>
              <p>{deployment.error.message}</p>
            </div>
          </div>
          <Button onClick={() => void action('retry')} disabled={busy}>
            Retry deployment
          </Button>
        </div>
      ) : null}
      <div className="deployment-grid">
        <section className="panel">
          <div className="panel__header">
            <div>
              <span className="overline">Append-only events</span>
              <h2>Deployment timeline</h2>
            </div>
            <Button className="button--quiet button--small" onClick={() => void reload()}>
              Refresh
            </Button>
          </div>
          <ol className="timeline">
            {deployment.events.map((event, index) => (
              <li
                key={event.id}
                className={index === deployment.events.length - 1 ? 'timeline__current' : ''}
              >
                <span />
                <div>
                  <strong>{event.state.replaceAll('_', ' ')}</strong>
                  <p>{event.reason ?? 'State recorded.'}</p>
                  <small>
                    {new Date(event.createdAt).toLocaleString()} · {event.source}
                  </small>
                </div>
              </li>
            ))}
          </ol>
        </section>
        <section className="panel">
          <div className="panel__header">
            <div>
              <span className="overline">Provider boundary</span>
              <h2>Resource controls</h2>
            </div>
          </div>
          <dl className="facts facts--stack">
            <div>
              <dt>Provider</dt>
              <dd>{deployment.providerName}</dd>
            </div>
            <div>
              <dt>Last update</dt>
              <dd>{new Date(deployment.updatedAt).toLocaleString()}</dd>
            </div>
            <div>
              <dt>Dashboard</dt>
              <dd>
                {deployment.dashboardUrl ? (
                  <a href={deployment.dashboardUrl} target="_blank" rel="noreferrer">
                    Open provider dashboard ↗
                  </a>
                ) : (
                  'Pending resource creation'
                )}
              </dd>
            </div>
          </dl>
          <div className="control-stack">
            <Button className="button--quiet" onClick={() => void loadLogs()} disabled={busy}>
              View provider logs
            </Button>
            {deployment.state === 'BUILDING' ? (
              <Button
                className="button--quiet"
                onClick={() => void action('refresh')}
                disabled={busy}
              >
                Refresh provider status
              </Button>
            ) : null}
            {deployment.state === 'LIVE' ? (
              <Button
                className="button--quiet"
                onClick={() => void action('redeploy')}
                disabled={busy}
              >
                Redeploy commit
              </Button>
            ) : null}
            {['LIVE', 'FAILED'].includes(deployment.state) ? (
              <Button className="button--danger-quiet" onClick={() => setConfirmDelete(true)}>
                Delete provider resource
              </Button>
            ) : null}
          </div>
        </section>
      </div>
      {logs ? (
        <section className="panel log-panel">
          <div className="panel__header">
            <div>
              <span className="overline">Redacted output</span>
              <h2>Provider logs</h2>
            </div>
            <Button className="button--quiet button--small" onClick={() => setLogs(null)}>
              Close
            </Button>
          </div>
          {logs.length ? (
            <pre>{logs.map((line) => `${line.timestamp ?? '—'}  ${line.message}`).join('\n')}</pre>
          ) : (
            <EmptyState title="No log lines returned">
              The provider may not expose logs for this resource type or stage.
            </EmptyState>
          )}
        </section>
      ) : null}
      {confirmDelete ? (
        <div className="modal-backdrop" role="presentation">
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="delete-title">
            <span className="danger-icon">!</span>
            <h2 id="delete-title">Delete the provider resource?</h2>
            <p>
              This removes <strong>{deployment.serviceName}</strong> from {deployment.providerName}.
              It does not delete the GitHub repository.
            </p>
            <Field label={`Type ${deployment.serviceName} to confirm`}>
              <input
                autoFocus
                value={deleteText}
                onChange={(event) => setDeleteText(event.target.value)}
              />
            </Field>
            <div className="modal__actions">
              <Button className="button--quiet" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button
                className="button--danger"
                disabled={deleteText !== deployment.serviceName || busy}
                onClick={() => void deleteResource()}
              >
                Delete resource
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Settings({
  connections,
  reload,
  onClose,
}: {
  connections: ProviderConnectionView[];
  reload: () => Promise<void>;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [error, setError] = useState<unknown>(null);
  async function disconnect(id: string) {
    try {
      await remove(`/api/providers/connections/${id}`);
      await reload();
    } catch (next) {
      setError(next);
    }
  }
  async function logout() {
    try {
      await post('/api/auth/logout', {});
      void navigate('/');
      window.location.reload();
    } catch (next) {
      setError(next);
    }
  }
  return (
    <div className="settings-page">
      <div className="step-title">
        <div>
          <span>Owner settings</span>
          <h1>Connections and session</h1>
          <p>Disconnecting a provider never silently deletes its existing resources.</p>
        </div>
        <Button className="button--quiet" onClick={onClose}>
          Back to deployment
        </Button>
      </div>
      {error ? <ErrorNotice error={error} /> : null}
      <section className="panel">
        <div className="panel__header">
          <div>
            <span className="overline">Provider credentials</span>
            <h2>Connected accounts</h2>
          </div>
        </div>
        {connections.length ? (
          <div className="settings-list">
            {connections.map((item) => (
              <div key={item.id}>
                <span className="provider-symbol">{item.provider === 'render' ? 'R' : 'CF'}</span>
                <span>
                  <strong>{item.providerName}</strong>
                  <small>{item.accountLabel}</small>
                </span>
                <Button
                  className="button--danger-quiet button--small"
                  onClick={() => void disconnect(item.id)}
                >
                  Disconnect
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState title="No provider connections">
            Connect one during the deployment workflow.
          </EmptyState>
        )}
      </section>
      <section className="panel">
        <div className="panel__header">
          <div>
            <span className="overline">Session</span>
            <h2>GitHub owner session</h2>
          </div>
        </div>
        <p className="muted">
          Signing out revokes this server-side session. It does not revoke the GitHub App
          installation or provider credentials.
        </p>
        <Button className="button--quiet" onClick={() => void logout()}>
          Sign out
        </Button>
      </section>
    </div>
  );
}

export function ConsolePage() {
  const [session, setSession] = useState<SessionView | null>(null);
  const [setup, setSetup] = useState<SetupState | null>(null);
  const [installations, setInstallations] = useState<GitHubInstallationView[]>([]);
  const [connections, setConnections] = useState<ProviderConnectionView[]>([]);
  const [capabilities, setCapabilities] = useState<ProviderCapability[]>([]);
  const [step, setStep] = useState<Step>('repository');
  const [analysisResponse, setAnalysisResponse] = useState<AnalysisResponse | null>(null);
  const [serviceIndex, setServiceIndex] = useState(0);
  const [corrections, setCorrections] = useState<Record<string, string | null>>({});
  const [selectedConnection, setSelectedConnection] = useState('');
  const [environment, setEnvironment] = useState<Record<string, string>>({});
  const [autoDeploy, setAutoDeploy] = useState(true);
  const [plan, setPlan] = useState<DeploymentPlan | null>(null);
  const [deployment, setDeployment] = useState<DeploymentView | null>(null);
  const [settings, setSettings] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const reloadConnections = useCallback(
    async () => setConnections(await api('/api/providers/connections')),
    [],
  );
  useEffect(() => {
    void Promise.all([
      loadSession(),
      api<SetupState>('/api/setup'),
      api<ProviderCapability[]>('/api/providers/capabilities'),
    ])
      .then(async ([nextSession, nextSetup, nextCapabilities]) => {
        setSession(nextSession);
        setSetup(nextSetup);
        setCapabilities(nextCapabilities);
        if (nextSession.authenticated) {
          const [nextInstallations, nextConnections] = await Promise.all([
            api<GitHubInstallationView[]>('/api/github/installations'),
            api<ProviderConnectionView[]>('/api/providers/connections'),
          ]);
          setInstallations(nextInstallations);
          setConnections(nextConnections);
          const remembered = localStorage.getItem('repohost:last-deployment');
          if (remembered) {
            try {
              const view = await api<DeploymentView>(`/api/deployments/${remembered}`);
              setDeployment(view);
              setStep('deployment');
            } catch {
              localStorage.removeItem('repohost:last-deployment');
            }
          }
        }
      })
      .catch(setError)
      .finally(() => setLoading(false));
  }, []);
  const deploymentId = deployment?.id;
  const deploymentState = deployment?.state;
  const reloadDeployment = useCallback(async () => {
    if (!deploymentId) return;
    setDeployment(await api(`/api/deployments/${deploymentId}`));
  }, [deploymentId]);
  useEffect(() => {
    if (
      !deploymentId ||
      !deploymentState ||
      !['QUEUED', 'PROVISIONING', 'BUILDING', 'DELETE_REQUESTED', 'DELETING'].includes(
        deploymentState,
      )
    )
      return;
    const timer = window.setInterval(() => void reloadDeployment().catch(setError), 4000);
    return () => window.clearInterval(timer);
  }, [deploymentId, deploymentState, reloadDeployment]);
  async function install() {
    const value = await api<{ url: string }>('/api/github/install-url');
    window.location.assign(value.url);
  }
  function analysed(value: AnalysisResponse) {
    setAnalysisResponse(value);
    setServiceIndex(0);
    setCorrections({});
    setEnvironment(
      Object.fromEntries(
        (value.analysis.services[0]?.environmentVariables ?? []).map((item) => [item.name, '']),
      ),
    );
    setStep('analysis');
  }
  const service = analysisResponse?.analysis.services[serviceIndex] ?? null;
  const providerForConnection = () =>
    connections.find((item) => item.id === selectedConnection)?.provider;
  async function createPlan() {
    if (!analysisResponse || !service || !selectedConnection) return;
    setActionLoading(true);
    setError(null);
    try {
      const value = await post<DeploymentPlan>('/api/plans', {
        projectId: analysisResponse.projectId,
        analysisId: analysisResponse.analysisIds[serviceIndex],
        providerConnectionId: selectedConnection,
        provider: providerForConnection(),
        overrides: { ...corrections, autoDeploy },
        environment: Object.entries(environment).map(([name, value]) => ({
          name,
          value,
          secret:
            service.environmentVariables.find((item) => item.name === name)?.likelySecret ?? true,
        })),
      });
      setPlan(value);
      setEnvironment({});
      setStep('review');
    } catch (next) {
      setError(next);
    } finally {
      setActionLoading(false);
    }
  }
  async function confirm() {
    if (!plan) return;
    setActionLoading(true);
    setError(null);
    try {
      const value = await post<DeploymentView>(
        `/api/plans/${plan.id}/confirm`,
        { digest: plan.digest },
        { 'Idempotency-Key': crypto.randomUUID() },
      );
      setDeployment(value);
      localStorage.setItem('repohost:last-deployment', value.id);
      setStep('deployment');
    } catch (next) {
      setError(next);
    } finally {
      setActionLoading(false);
    }
  }
  if (loading)
    return (
      <main className="app-shell">
        <div className="loading-page">
          <Mark />
          <Skeleton lines={5} />
        </div>
      </main>
    );
  if (!session?.authenticated)
    return (
      <main className="auth-gate" id="main-content">
        <Mark />
        <h1>Owner authentication required</h1>
        <p>Sign in with a GitHub identity present in the operator allowlist.</p>
        <a className="button" href="/api/auth/github/start?redirect=%2Fapp">
          Sign in with GitHub
        </a>
        {error ? <ErrorNotice error={error} /> : null}
      </main>
    );
  if (setup && !setup.ready)
    return (
      <main className="app-shell">
        <Header session={session} onSettings={() => setSettings(true)} />
        <SetupChecklist setup={setup} />
      </main>
    );
  const currentIndex = STEPS.findIndex((candidate) => candidate.key === step);
  return (
    <main className="app-shell">
      <Header session={session} onSettings={() => setSettings(true)} />
      <div className="workspace">
        <aside className="stepper" aria-label="Deployment progress">
          <span className="stepper__label">New deployment</span>
          <ol>
            {STEPS.map((item, index) => (
              <li
                key={item.key}
                className={
                  item.key === step
                    ? 'stepper__active'
                    : index < currentIndex
                      ? 'stepper__done'
                      : ''
                }
              >
                <span>{index < currentIndex ? '✓' : index + 1}</span>
                <strong>{item.label}</strong>
              </li>
            ))}
          </ol>
          <div className="stepper__boundary">
            <strong>Trust boundary</strong>
            <p>RepoHost reads selected metadata. Your provider builds the source.</p>
          </div>
        </aside>
        <section className="workspace__main" id="main-content">
          {settings ? (
            <Settings
              connections={connections}
              reload={reloadConnections}
              onClose={() => setSettings(false)}
            />
          ) : step === 'repository' ? (
            <RepositoryStep
              installations={installations}
              onInstall={install}
              onAnalysed={analysed}
            />
          ) : step === 'analysis' && analysisResponse ? (
            <AnalysisStep
              response={analysisResponse}
              serviceIndex={serviceIndex}
              setServiceIndex={(value) => {
                setServiceIndex(value);
                setCorrections({});
                setEnvironment(
                  Object.fromEntries(
                    (analysisResponse.analysis.services[value]?.environmentVariables ?? []).map(
                      (item) => [item.name, ''],
                    ),
                  ),
                );
              }}
              corrections={corrections}
              setCorrections={setCorrections}
              next={() => setStep('provider')}
            />
          ) : step === 'provider' && service ? (
            <ProviderStep
              service={service}
              capabilities={capabilities}
              connections={connections}
              reload={reloadConnections}
              selectedConnection={selectedConnection}
              setSelectedConnection={setSelectedConnection}
              next={() => setStep('configuration')}
            />
          ) : step === 'configuration' && service ? (
            <ConfigurationStep
              service={service}
              values={environment}
              setValues={setEnvironment}
              autoDeploy={autoDeploy}
              setAutoDeploy={setAutoDeploy}
              onCreatePlan={() => void createPlan()}
              loading={actionLoading}
              error={error}
            />
          ) : step === 'review' && plan ? (
            <ReviewStep
              plan={plan}
              onConfirm={() => void confirm()}
              loading={actionLoading}
              error={error}
            />
          ) : step === 'deployment' && deployment ? (
            <DeploymentStep deployment={deployment} reload={reloadDeployment} />
          ) : (
            <EmptyState title="Workflow state unavailable">
              Return to the repository step and start a fresh analysis.
            </EmptyState>
          )}
        </section>
      </div>
    </main>
  );
}
